import {
  ArrayLayoutProps,
  composePaths,
  createDefaultValue,
  Generate,
  isObjectArrayControl,
  isPrimitiveArrayControl,
  JsonSchema,
  or,
  rankWith,
  RankedTester,
} from "@jsonforms/core";
import {
  JsonFormsDispatch,
  withJsonFormsArrayLayoutProps,
} from "@jsonforms/react";
import { useCallback, useMemo } from "react";

import FieldLabel from "@/components/primitives/InputLabel";
import { InputError } from "@/components/primitives/InputError";
import { IconButton, SecondaryButton } from "@/components/elements/Button";
import { XIcon } from "lucide-react";

export const arrayControlTester: RankedTester = rankWith(
  3,
  or(isObjectArrayControl, isPrimitiveArrayControl),
);

function ArrayControlComponent({
  data,
  path,
  schema,
  arraySchema,
  rootSchema,
  uischema: _uischema,
  renderers,
  cells,
  label,
  visible,
  enabled,
  required,
  description,
  errors,
  addItem,
  removeItems,
}: ArrayLayoutProps) {
  const hasError = errors !== undefined && errors.length > 0;
  // `data` for an array *layout* control (unlike an array *control*) is the
  // item count, not the array itself - JsonForms resolves each item's data
  // independently by path/index (see `mapStateToArrayLayoutProps`).
  const count = typeof data === "number" ? data : 0;
  const maxItems = arraySchema.maxItems;

  const handleAdd = useCallback(() => {
    const value = createDefaultValue(schema, rootSchema);
    addItem?.(path, value)();
  }, [addItem, path, schema, rootSchema]);

  const handleRemove = useCallback(
    (index: number) => {
      removeItems?.(path, [index])();
    },
    [removeItems, path],
  );

  const isPrimitive =
    schema?.type === "string" ||
    schema?.type === "number" ||
    schema?.type === "integer" ||
    schema?.type === "boolean";

  const itemUiSchema = useMemo(
    () => (isPrimitive ? undefined : Generate.uiSchema(schema as JsonSchema)),
    [isPrimitive, schema],
  );

  if (!visible) return null;

  return (
    <div>
      {(label || description) && (
        <div className="mb-2">
          {label && (
            <FieldLabel className="text-sm" required={required}>
              {label}
            </FieldLabel>
          )}
          {description && count > 0 && (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </div>
      )}

      {count ? (
        <div className="flex flex-col gap-4">
          {Array.from({ length: count }, (_, index) => {
            const childPath = composePaths(path, `${index}`);
            const canRemove =
              enabled &&
              (!arraySchema.minItems || count > arraySchema.minItems);

            return (
              <div
                key={index}
                className="relative rounded-md border bg-background p-4"
              >
                {isPrimitive ? (
                  <PrimitiveArrayItem
                    path={childPath}
                    schema={schema}
                    renderers={renderers}
                    cells={cells}
                    enabled={enabled}
                  />
                ) : (
                  <JsonFormsDispatch
                    schema={schema}
                    uischema={itemUiSchema}
                    path={childPath}
                    renderers={renderers}
                    cells={cells}
                    enabled={enabled}
                  />
                )}
                {canRemove && (
                  <div className="absolute top-2 right-2">
                    <IconButton
                      size="xs"
                      icon={<XIcon />}
                      variant="ghost"
                      aria-label="Remove item"
                      onClick={() => handleRemove(index)}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {enabled && (
            <SecondaryButton
              className="w-fit"
              disabled={!!maxItems && count >= maxItems}
              onClick={handleAdd}
            >
              Add item{maxItems ? ` (${count}/${maxItems})` : ""}
            </SecondaryButton>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed p-8 text-center dark:bg-gray-900/20">
          <p className="text-sm font-medium">
            No items yet{required && " - you need to add at least one"}
          </p>
          {description && (
            <p className="max-w-md text-sm text-muted-foreground">
              {description}
            </p>
          )}
          {enabled && (
            <SecondaryButton onClick={handleAdd}>
              + Add your first item
            </SecondaryButton>
          )}
        </div>
      )}

      {hasError && errors !== "is a required property" && (
        <InputError className="mt-1">{errors}</InputError>
      )}
    </div>
  );
}

function PrimitiveArrayItem({
  path,
  schema,
  renderers,
  cells,
  enabled,
}: {
  path: string;
  schema: any;
  renderers: any;
  cells: any;
  enabled?: boolean;
}) {
  return (
    <JsonFormsDispatch
      schema={schema}
      path={path}
      renderers={renderers}
      cells={cells}
      enabled={enabled}
    />
  );
}

export const ArrayControl = withJsonFormsArrayLayoutProps(
  ArrayControlComponent,
);
